<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8 ">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.user-lock-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('User roles') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <router-link class="btn btn-app rounded-none rounded-l-md" to="/admin/user-roles/new">
            {{ $t('Create') }}
          </router-link>
          <button @click.prevent="$router.back()" type="button" class="btn btn-app rounded-none rounded-r-md">
            {{ $t('Back') }}
          </button>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8 ">
      <div class="content-card-body">
        <loading :status="loading"></loading>
        <div class="bg-white p-4 md:w-2/3 items-center md:flex md:justify-between sm:px-6">
          <!-- component -->
          <input
            id="search"
            v-model.lazy="search"
            :placeholder="$t('Search')"
            @change="getUserRoles"
            type="text"
            class="form-input rounded-none rounded-l-md"
          />
          <div class="flex relative w-full">
            <select @change="changeSort" aria-label="Sort by" class="rounded-none form-input" id="status" v-model="sort.column">
              <option value="name">{{ $t('Name') }}</option>
              <option value="updated_at">{{ $t('Updated at') }}</option>
              <option value="created_at">{{ $t('Created at') }}</option>
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-blue-400">
              <svg class="fill-current h-6 w-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"></path>
              </svg>
            </div>
          </div>
          <button @click="changeSort" class="flex text-gray-800 rounded-none rounded-r-md font-bold py-2 border border-gray-300 px-6" type="button">
            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.sort-amount-down-alt-regular" v-show="sort.order === 'asc'"></svg-vue>
            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.sort-amount-up-alt-regular" v-show="sort.order === 'desc'"></svg-vue>
            <span class="ml-2">{{ $t('Sort') }}</span>
          </button>
        </div>
        <div class="border-t border-gray-200">
          <template v-if="userRoleList.length > 0">
            <ul>
              <template v-for="(userRole, index) in userRoleList">
                <li :class="{ 'border-t border-gray-200': index !== 0 }" :key="index">
                  <template v-if="userRole.is_primary">
                    <div class="block cursor-not-allowed focus:outline-none focus:bg-blue-100 transition duration-150 ease-in-out">
                      <div class="flex items-center px-4 py-6 sm:px-6">
                        <div class="min-w-0 flex-1 flex items-center">
                          <div class="min-w-0 flex-1 md:grid md:grid-cols-2 md:gap-4">
                            <div>
                              <div class="text-sm leading-5 font-medium text-green-600 truncate">
                                {{ userRole.name }}
                              </div>
                            </div>
                            <div class="hidden md:block">
                              <div class="text-sm leading-5 text-gray-900">
                                <span class="text-cool-gray-900 font-medium">{{ userRole.users }}</span>
                                {{ $t('assigned users') }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="w-7"></div>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <router-link
                      :to="'/admin/user-roles/' + userRole.id + '/edit'"
                      class="block hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                    >
                      <div class="flex items-center px-4 py-6 sm:px-6">
                        <div class="min-w-0 flex-1 flex items-center">
                          <div class="min-w-0 flex-1 md:grid md:grid-cols-2 md:gap-4">
                            <div>
                              <div class="text-sm leading-5 font-medium text-blue-600 truncate">
                                {{ userRole.name }}
                              </div>
                            </div>
                            <div class="hidden md:block">
                              <div class="text-sm leading-5 text-gray-900">
                                <span class="text-cool-gray-900 font-medium">
                                  {{ userRole.users }}
                                </span>
                                {{ $t('assigned users') }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div>
                          <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.angle-right-regular"></svg-vue>
                        </div>
                      </div>
                    </router-link>
                  </template>
                </li>
              </template>
            </ul>
          </template>
          <template v-else>
            <no-content/>
          </template>
        </div>
        <div class="hidden sm:block">
          <nav class="bg-white p-4 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <p class="text-sm leading-5 text-gray-700">
              {{ $t('Showing') }}
              <span class="font-medium">{{ userRoleList.length }}</span>
              {{ $t('user roles') }}
            </p>
          </nav>
        </div>
      </div>
    </div>
  </main>
</template>
<script>
export default {
  name: 'list-user-roles',
  metaInfo() {
    return {
      title: this.$i18n.t('User roles'),
    };
  },
  data() {
    return {
      search: '',
      sort: {
        order: 'asc',
        column: 'created_at',
      },
      loading: true,
      userRoleList: [],
    };
  },
  mounted() {
    this.getUserRoles();
  },
  methods: {
    changeSort() {
      if (this.sort.order === 'asc') {
        this.sort.order = 'desc';
      } else if (this.sort.order === 'desc') {
        this.sort.order = 'asc';
      }
      this.getUserRoles();
    },
    getUserRoles() {
      this.loading = true;
      axios
        .get(route('user-roles.index'), {
          params: {
            sort: this.sort,
            search: this.search,
          },
        })
        .then((response) => {
          this.userRoleList = response.data.items;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
